import React, { useState } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Cylinder } from "lucide-react";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

function getResetToken() {
  const params = new URLSearchParams(window.location.search);
  return params.get("reset");
}

function clearResetToken() {
  window.history.replaceState({}, "", window.location.pathname);
}

function Gate() {
  const { user } = useAuth();
  const [resetToken, setResetToken] = useState(getResetToken());
  const [authView, setAuthView] = useState("login"); // login | forgot

  if (resetToken) {
    return (
      <ResetPassword
        token={resetToken}
        onDone={() => {
          clearResetToken();
          setResetToken(null);
          setAuthView("login");
        }}
      />
    );
  }

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0B0F17] bg-grid flex items-center justify-center" data-testid="auth-loading">
        <div className="flex flex-col items-center gap-3">
          <Cylinder className="h-8 w-8 text-amber-400 animate-pulse" />
          <p className="text-xs font-mono text-slate-500">Loading…</p>
        </div>
      </div>
    );
  }
  if (user) return <App />;
  if (authView === "forgot") return <ForgotPassword onBack={() => setAuthView("login")} />;
  return <Login onForgot={() => setAuthView("forgot")} />;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
